import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useAuth } from "../../contexts/AuthContext";
import { useApiClient } from "../../contexts/ApiClientContext";
import Annotation from "../../model/Annotation";
import PagedData from "../../model/PagedData";
import APIEndPoints from "../../utils/APIEndPoints";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import Loading from "../common/Loading";
import UserRoles from "../../model/enums/UserRole";
import AnnotationsListElement from "./AnnotationsListElement";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";

function AnnotationsList() {
  // read the ID from the url (_3DInstance ID)
  const { id } = useParams();
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the navigator
  const navigate = useNavigate();
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // activities information to be displayed
  const [data, setData] = useState<Annotation[]>([]);
  const [showMore, setShowMore] = useState(false);
  // manage data loading animation
  const [loading, setLoading] = useState(true);

  // load record data from the server
  useEffect(() => {
    apiClient
      ?.get<PagedData<Annotation[]>>({
        url: APIEndPoints.ANNOTATIONS.PAGED(1, 5, parseInt(id!)),
        requiresToken: true,
      })
      .then((response: PagedData<Annotation[]>) => {
        setData(response.items.slice(0, 4));
        setShowMore(response.items.length > 4);
      })
      .catch(() => {
        navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [apiClient, id, navigate]);

  const handleShowMore = () => {
    apiClient
      ?.get<PagedData<Annotation[]>>({
        url: APIEndPoints.ANNOTATIONS.ALL(parseInt(id!)),
        requiresToken: true,
      })
      .then((response: PagedData<Annotation[]>) => {
        setData(response.items);
        setShowMore(false);
      })
      .catch(() => {
        navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="details-grid">
      {data?.length > 0 &&
        data?.map((item) => {
          const canEdit =
            !EmbeddingToolInfo.isChemiTool() &&
            (item.id_user === authManager.getUserID() ||
              authManager.getUserRole() === UserRoles.ADMIN);
          return (
            <AnnotationsListElement
              data={item}
              key={item.id}
              canEdit={canEdit}
            />
          );
        })}

      {showMore && (
        <div className="details-show-more">
          <Button
            label="Show all Annotations"
            onClick={handleShowMore}
            purpose={ButtonAction.UPDATE}
          />
        </div>
      )}

      {data?.length === 0 && (
        <div className="details-no-records">No annotations available.</div>
      )}
    </div>
  );
}

export default AnnotationsList;
